import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Building2,
  CreditCard,
  Edit3,
  Landmark,
  MoreVertical,
  ReceiptIndianRupee,
  Search,
  Trash2,
  WalletCards,
} from "lucide-react";
import { AES, enc } from "crypto-js";
import swal from "sweetalert";
import { BASE_URL } from "../CommonApi";
import BankUpdate from "./BankUpdate";
import "../../../Components/Dashboard/PremiumListPage.css";

const decodeUniqueId = () => AES.decrypt(localStorage.getItem("uniqueId1"), "1234567812345678").toString(enc.Utf8);

const normalizeRows = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  return Object.values(value).find(Array.isArray) || [];
};

const maskAccount = (value) => {
  const account = String(value || "").replace(/\s/g, "");
  if (!account) return "-";
  if (account.length <= 4) return account;
  return `${"•".repeat(Math.min(account.length - 4, 8))}${account.slice(-4)}`;
};

const money = (value) => `₹ ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const getBalance = (bank = {}) =>
  bank.currentBalance || bank.balance || bank.openingBalance || bank.amountInBank || bank.amountInBooks || 0;

const getStatus = (bank = {}) => bank.status || bank.activeStatus || (bank.primaryAccount === "YES" ? "Primary" : "Active");

const BankStatus = ({ bank }) => {
  const status = getStatus(bank);
  const tone = String(status).toLowerCase().includes("inactive") ? "red" : String(status).toLowerCase().includes("primary") ? "blue" : "green";
  return <span className={`premium-status ${tone}`}>{status}</span>;
};

function BankAcc({ navigation }) {
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [editBankId, setEditBankId] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const jwtToken = localStorage.getItem("jwtToken");

  const fetchBanks = () => {
    setLoading(true);
    fetch(BASE_URL + "/fetchBanks", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ uniqueId: decodeUniqueId() }),
    })
      .then((response) => response.json())
      .then((data) => setBanks(normalizeRows(data.responseMessage || data)))
      .catch((error) => console.error("Error fetching banks:", error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const filteredBanks = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return banks;
    return banks.filter((bank) =>
      [bank.bankName, bank.name, bank.accountNumber, bank.ifsc, bank.branchName, bank.accountType]
        .some((value) => String(value || "").toLowerCase().includes(search))
    );
  }, [banks, query]);

  const deleteBank = (bank) => {
    if (!window.confirm("Are you sure? Once deleted, you will not be able to recover this bank account!")) return;
    fetch(BASE_URL + `/deleteBank/${bank.id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ uniqueId: decodeUniqueId() }),
    })
      .then((response) => {
        if (response.ok) {
          setBanks((current) => current.filter((item) => item.id !== bank.id));
          if (selectedBank?.id === bank.id) setSelectedBank(null);
          swal({ icon: "success", title: "Bank Deleted Successfully" });
        }
      })
      .catch((error) => console.error("Error deleting bank:", error));
  };

  if (editBankId) {
    return (
      <BankUpdate
        id={editBankId}
        handleCancle={() => {
          setEditBankId("");
          fetchBanks();
        }}
      />
    );
  }

  if (selectedBank) {
    return (
      <section className="premium-list-page bank-detail-page">
        <div className="premium-page-head bank-detail-head">
          <div className="bank-title-lockup">
            <button className="bank-back-btn" onClick={() => setSelectedBank(null)} aria-label="Back to banking">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1>{selectedBank.bankName || selectedBank.name || "Bank Account"}</h1>
              <p>Bank detail, linked transactions, and recent payments.</p>
            </div>
          </div>
          <button className="premium-primary-btn" onClick={() => setEditBankId(selectedBank.id)}>
            <Edit3 size={16} />
            Edit Bank
          </button>
        </div>

        <div className="bank-detail-grid">
          <article className="bank-hero-card">
            <span className="bank-hero-icon">
              {String(selectedBank.accountType || "").toLowerCase().includes("credit") ? <CreditCard size={28} /> : <Landmark size={28} />}
            </span>
            <div>
              <small>Current Balance</small>
              <strong>{money(getBalance(selectedBank))}</strong>
              <BankStatus bank={selectedBank} />
            </div>
          </article>

          <article className="bank-info-card">
            <h3>Account Information</h3>
            <dl>
              <div><dt>Account Holder</dt><dd>{selectedBank.name || "-"}</dd></div>
              <div><dt>Account Number</dt><dd>{selectedBank.accountNumber || "-"}</dd></div>
              <div><dt>IFSC Code</dt><dd>{selectedBank.ifsc || "-"}</dd></div>
              <div><dt>Branch</dt><dd>{selectedBank.branchName || "-"}</dd></div>
              <div><dt>Account Type</dt><dd>{selectedBank.accountType || "-"}</dd></div>
              <div><dt>Opening Balance</dt><dd>{money(selectedBank.openingBalance)}</dd></div>
              <div><dt>Created Date</dt><dd>{selectedBank.createdDate || selectedBank.date || "-"}</dd></div>
            </dl>
          </article>

          <article className="bank-info-card">
            <h3>Linked Transactions</h3>
            <div className="bank-empty-panel">
              <ReceiptIndianRupee size={22} />
              <strong>No linked transactions</strong>
              <p>Transactions connected to this account will appear here.</p>
            </div>
          </article>

          <article className="bank-info-card">
            <h3>Recent Payments</h3>
            <div className="bank-empty-panel">
              <WalletCards size={22} />
              <strong>No recent payments</strong>
              <p>Recent payment activity will appear once available.</p>
            </div>
          </article>
        </div>
      </section>
    );
  }

  if (loading) return <div className="premium-empty-state">Loading bank accounts...</div>;

  return (
    <section className="premium-list-page bank-list-page">
      <div className="premium-page-head">
        <div>
          <h1>Banking</h1>
          <p>Manage bank accounts, account types, balances, and payment channels.</p>
        </div>
        <button className="premium-primary-btn" onClick={() => navigation.navigate("BankAccNew")}>
          + New Bank Account
        </button>
      </div>

      <div className="bank-toolbar">
        <Search size={18} />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search bank accounts..." />
      </div>

      <div className="bank-card-grid">
        {filteredBanks.map((bank) => (
          <article className="bank-account-card" key={bank.id} onClick={() => setSelectedBank(bank)}>
            <div className="bank-account-top">
              <span className="bank-account-icon">
                {String(bank.accountType || "").toLowerCase().includes("credit") ? <CreditCard size={20} /> : <Building2 size={20} />}
              </span>
              <BankStatus bank={bank} />
            </div>
            <button className="bank-card-title" onClick={(event) => { event.stopPropagation(); setSelectedBank(bank); }}>
              {bank.bankName || bank.name || "Bank Account"}
            </button>
            <p>{bank.name || "Account holder not added"}</p>
            <div className="bank-card-meta">
              <span>Account</span>
              <strong>{maskAccount(bank.accountNumber)}</strong>
            </div>
            <div className="bank-card-meta">
              <span>IFSC</span>
              <strong>{bank.ifsc || "-"}</strong>
            </div>
            <div className="bank-card-meta">
              <span>Branch</span>
              <strong>{bank.branchName || "-"}</strong>
            </div>
            <div className="bank-card-footer">
              <div>
                <small>Current Balance</small>
                <strong>{money(getBalance(bank))}</strong>
              </div>
              <div className="premium-row-actions" onClick={(event) => event.stopPropagation()}>
                <button onClick={() => setEditBankId(bank.id)} title="Edit"><Edit3 size={16} /></button>
                <button onClick={() => deleteBank(bank)} title="Delete"><Trash2 size={16} /></button>
                <button title="More"><MoreVertical size={16} /></button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {filteredBanks.length === 0 && <div className="premium-empty-state">No bank accounts found</div>}
    </section>
  );
}

export default BankAcc;
